import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Wallet, Clock, BookOpen, Star, ArrowRight, Play, User, Calendar,
  AlertCircle, ShieldCheck, LayoutDashboard, Search, Users, LogOut, Key, Home
} from 'lucide-react';
import api from '../../api/axios';
import StatCard from '../../components/StatCard';
import StatusBadge from '../../components/StatusBadge';
import WithdrawModal from './WithdrawModal'; 
import { useTranslation } from 'react-i18next';

interface Job {
  id: string;
  studentId: string;
  studentName: string;
  subject: string;
  studentBalance: number;
  canStart: boolean;
}

interface TutorStats {
  totalHours: number;
  rating: number;
}

const TutorHome: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [acceptedJobs, setAcceptedJobs] = useState<Job[]>([]);
  const [stats, setStats] = useState<TutorStats>({ totalHours: 0, rating: 0 });
  const [loading, setLoading] = useState(true);

  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);

  const [passwordData, setPasswordData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordStatus, setPasswordStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const [jobsRes, statsRes] = await Promise.all([
          api.get('/tutor/jobs/accepted'),
          api.get('/tutor/stats')
        ]);
        setAcceptedJobs(jobsRes.data);
        setStats(statsRes.data);
      } catch (err) {
        console.error("Failed to load tutor home data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHomeData();
  }, []);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return setPasswordStatus({ type: 'error', message: t('passwords_do_not_match', 'New passwords do not match') });
    }

    try {
      await api.post('/auth/change-password', {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordStatus({ type: 'success', message: t('password_updated_terminating', 'Password updated! Terminating session...') });
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });

      setTimeout(() => {
        logout();
        navigate('/login?reason=password_changed');
      }, 2000);

    } catch (err: any) {
      setPasswordStatus({
        type: 'error',
        message: err.response?.data?.message || t('password_auth_failed', 'Password change authorization failed')
      });
    }
  };

  return (
    <div className="w-full min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 text-white p-4 md:p-8 pb-32 relative">

      <header className="h-16 shrink-0 bg-slate-900/80 backdrop-blur-md border-b border-slate-800 px-4 lg:px-6 flex items-center justify-between z-30 shadow-2xl mb-6 rounded-2xl">
        <div className="flex items-center gap-4 lg:gap-8">
          <Link to="/tutor/home" className="flex items-center gap-3 cursor-pointer">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center font-black italic shadow-lg shadow-blue-600/20 text-white hover:scale-105 transition-transform">T</div>
            <div className="hidden sm:block">
              <span className="font-black text-[11px] block leading-none italic uppercase tracking-tighter text-white">
                {t('app_name', 'AtronsTutor')} <span className="text-blue-500">{t('console', 'Console')}</span>
              </span>
            </div>
          </Link>

          <nav className="flex items-center gap-1 sm:gap-2 border-l border-slate-800 pl-4 sm:pl-6">
            <Link to="/tutor/home" className={`text-[10px] font-black uppercase flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${window.location.pathname === '/tutor/home' ? 'text-blue-400 bg-blue-500/10' : 'text-slate-400 hover:text-white'}`}>
              <LayoutDashboard size={14} /> <span className="hidden md:block">{t('home_nav', 'Home')}</span>
            </Link>
            <Link to="/tutor/jobs/browse" className={`text-[10px] font-black uppercase flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${window.location.pathname === '/tutor/jobs/browse' ? 'text-blue-400 bg-blue-500/10' : 'text-slate-400 hover:text-white'}`}>
              <Search size={14} /> <span className="hidden md:block">{t('browse_nav', 'Browse')}</span>
            </Link>
            <Link to="/tutor/my-classes" className={`text-[10px] font-black uppercase flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${window.location.pathname === '/tutor/my-classes' ? 'text-blue-400 bg-blue-500/10' : 'text-slate-400 hover:text-white'}`}>
              <Users size={14} /> <span className="hidden md:block">{t('classes_nav', 'Classes')}</span>
            </Link>
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden lg:flex bg-slate-950 pl-4 pr-2 py-1.5 rounded-2xl border border-slate-800 items-center justify-center gap-3">
            <span className="text-xs font-black text-white">{Number(user?.walletBalance || 0).toFixed(2)} <span className="ml-1 text-[10px] text-slate-500">ETB</span></span>
            <button onClick={() => setIsWithdrawOpen(true)} className="bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all">
              {t('payout', 'Payout')}
            </button>
          </div>
          <button onClick={() => window.confirm(t('confirm_logout', "Logout?")) && logout()} className="p-2.5 text-slate-500 bg-slate-800 rounded-xl hover:bg-red-500/20 hover:text-red-500 transition-all border border-slate-700">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-950/80 backdrop-blur-sm p-6 rounded-3xl border border-slate-800 shadow-xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-600/10 text-blue-400 rounded-2xl flex items-center justify-center border border-blue-500/20">
            <User size={24} />
          </div>
          <div>
            <h1 className="text-xl font-black uppercase tracking-tight">{t('welcome_educator', 'Welcome Back, Educator')}</h1>
            <p className="text-xs text-slate-400 font-medium">{t('manage_active_requests', 'Manage your active tutoring requests and track your earnings.')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-center bg-slate-900 px-4 py-2 rounded-2xl border border-slate-800">
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('account_profile', 'Account Profile:')}</span>
          <StatusBadge active={true} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label={t('wallet_balance', 'Wallet Balance')} value={`${Number(user?.walletBalance || 0).toFixed(2)} ETB`} icon={<Wallet size={20} className="text-emerald-400" />} colorClass="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" />
        <StatCard label={t('pending_classrooms', 'Pending Classrooms')} value={acceptedJobs.length} icon={<BookOpen size={20} className="text-blue-400" />} colorClass="bg-blue-500/10 text-blue-400 border border-blue-500/20" />
        <StatCard label={t('total_hours_taught', 'Total Hours Taught')} value={`${stats.totalHours} hrs`} icon={<Clock size={20} className="text-purple-400" />} colorClass="bg-purple-500/10 text-purple-400 border border-purple-500/20" />
        <StatCard label={t('tutor_rating', 'Tutor Rating')} value={stats.rating > 0 ? `${stats.rating} / 5.0` : t('new_rating', 'New')} icon={<Star size={20} className="text-amber-400" />} colorClass="bg-amber-500/10 text-amber-400 border border-amber-500/20" />
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-950/80 backdrop-blur-sm p-6 rounded-[2rem] border border-slate-800 shadow-2xl flex flex-col min-h-[350px]">
          <h2 className="text-sm font-black uppercase tracking-widest text-blue-400 mb-4 flex items-center gap-2">
            <Calendar size={16} /> {t('accepted_launchpad', 'Accepted Launchpad')}
          </h2>

          {loading ? (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-xs font-bold uppercase tracking-widest animate-pulse">{t('syncing_classrooms', 'Syncing active classrooms...')}</div>
          ) : acceptedJobs.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-6 border border-dashed border-slate-800 rounded-2xl">
              <AlertCircle size={32} className="text-slate-600 mb-2" />
              <p className="text-xs font-black uppercase tracking-wider text-slate-400">{t('no_pending_jobs', 'No pending jobs found')}</p>
              <Link to="/tutor/jobs/browse" className="text-[10px] font-black uppercase text-blue-400 mt-2 hover:underline flex items-center gap-1">{t('browse_available_requests', 'Browse available student requests')} <ArrowRight size={12} /></Link>
            </div>
          ) : (
            <div className="space-y-3 overflow-y-auto max-h-[400px] pr-2 custom-scrollbar">
              {acceptedJobs.map((job) => (
                <div key={job.id} className="p-4 bg-slate-900 rounded-2xl border border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 group">
                  <div>
                    <span className="px-2.5 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/25 rounded-lg text-[9px] font-black uppercase block w-fit mb-2">{job.subject}</span>
                    <h3 className="text-sm font-black text-white group-hover:text-blue-400 transition-colors">{job.studentName}</h3>
                    <p className="text-[11px] text-slate-500 font-bold mt-0.5">{t('student_balance', 'Student Balance')}: {Number(job.studentBalance).toFixed(2)} ETB</p>
                  </div>
                  <button disabled={!job.canStart} onClick={() => navigate(`/tutor/dashboard?studentId=${job.studentId}&jobId=${job.id}`)} className={`w-full sm:w-auto px-5 py-3 rounded-xl font-black text-[10px] uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 shrink-0 ${job.canStart ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-slate-800 text-slate-600 cursor-not-allowed'}`}>
                    <Play size={12} fill="currentColor" /> {t('launch_classroom', 'Launch Classroom')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4 flex flex-col">
          <div className="bg-slate-950/80 backdrop-blur-sm p-6 rounded-[2rem] border border-slate-800 shadow-2xl flex flex-col">
            <h2 className="text-sm font-black uppercase tracking-widest text-blue-400 mb-4 flex items-center gap-2"><Key size={16} /> {t('account_security', 'Account Security')}</h2>
            {passwordStatus.message && (
              <div className={`p-3 rounded-xl text-[10px] font-black uppercase border mb-3 ${passwordStatus.type === 'success' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                {passwordStatus.message}
              </div>
            )}
            <form onSubmit={handlePasswordChange} className="space-y-3">
              <div>
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-wider block mb-1">{t('current_password', 'Current Password')}</label>
                <input type="password" value={passwordData.oldPassword} onChange={e => setPasswordData({ ...passwordData, oldPassword: e.target.value })} className="w-full bg-slate-900 border border-slate-800 px-3 py-2 text-xs text-white rounded-xl outline-none focus:border-blue-500 transition-colors" required />
              </div>
              <div>
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-wider block mb-1">{t('new_password', 'New Password')}</label>
                <input type="password" value={passwordData.newPassword} onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })} className="w-full bg-slate-900 border border-slate-800 px-3 py-2 text-xs text-white rounded-xl outline-none focus:border-blue-500 transition-colors" required />
              </div>
              <div>
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-wider block mb-1">{t('confirm_new_password', 'Confirm New Password')}</label>
                <input type="password" value={passwordData.confirmPassword} onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} className="w-full bg-slate-900 border border-slate-800 px-3 py-2 text-xs text-white rounded-xl outline-none focus:border-blue-500 transition-colors" required />
              </div>
              <button type="submit" className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-black uppercase tracking-wider text-[10px] transition-all active:scale-95 shadow-lg">{t('update_password', 'Update Password')}</button>
            </form>
          </div>

          <div className="bg-slate-950/80 backdrop-blur-sm p-6 rounded-[2rem] border border-slate-800 shadow-2xl flex-1 flex flex-col">
            <h2 className="text-sm font-black uppercase tracking-widest text-purple-400 mb-4 flex items-center gap-2"><ShieldCheck size={16} /> {t('quick_guide', 'Quick Guide')}</h2>
            <div className="space-y-4 text-xs text-slate-400 leading-relaxed font-medium">
              <p>{t('guide_1', '1. Ensure your external webcam and audio permissions are granted before initializing your workspace.')}</p>
              <p>{t('guide_2', '2. If your connection status slows down, turn on the Data Saver Mode switch built directly into the classroom header configuration.')}</p>
            </div>
          </div>
        </div>
      </div>

      <button onClick={() => navigate('/tutor/home')} className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-500 text-white p-4 rounded-full shadow-2xl shadow-blue-500/30 transition-all hover:-translate-y-1 z-50 group flex items-center gap-0 overflow-hidden">
        <Home size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-3 transition-all duration-300 ease-in-out whitespace-nowrap font-bold text-sm uppercase tracking-wider">{t('dashboard', 'Dashboard')}</span>
      </button>

      <WithdrawModal
        isOpen={isWithdrawOpen}
        onClose={() => setIsWithdrawOpen(false)}
        currentBalance={Number(user?.walletBalance || 0)}
        onSuccess={() => window.location.reload()}
      />

    </div>
  );
};

export default TutorHome;